import { jwtDecode } from 'jwt-decode';

export interface CognitoJWTPayload {
  sub: string;
  email_verified: boolean;
  'custom:CRPCEN'?: string;
  iss: string;
  'cognito:username': string;
  origin_jti: string;
  aud: string;
  event_id: string;
  token_use: 'id' | 'access';
  auth_time: number;
  'custom:partenaire_id'?: string;
  exp: number;
  iat: number;
  jti: string;
  email: string;
}

/**
 * Validates if a token is a valid JWT structure
 * @param token - The JWT token to validate
 * @returns boolean indicating if the token is valid
 */
export function isValidJWT(token: string): boolean {
  try {
    // Basic JWT structure check (3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Try to decode the token
    const decoded = jwtDecode(token);
    return decoded !== null && typeof decoded === 'object';
  } catch (error) {
    console.error('[JWT] Invalid JWT token:', error);
    return false;
  }
}

/**
 * Validates if a token is a valid AWS Cognito token
 * @param token - The JWT token to validate
 * @returns boolean indicating if the token is a valid Cognito token
 */
export function isValidCognitoToken(token: string): boolean {
  try {
    if (!isValidJWT(token)) {
      return false;
    }

    const decoded = jwtDecode<CognitoJWTPayload>(token);
    
    // Check for required Cognito fields
    const hasRequiredFields = 
      decoded.iss && 
      decoded.iss.includes('cognito-idp') &&
      decoded.aud &&
      decoded.token_use &&
      decoded.exp &&
      decoded.iat;

    if (!hasRequiredFields) {
      console.error('[JWT] Missing required Cognito fields in token');
      return false;
    }

    // Check if token is expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (decoded.exp < currentTime) {
      console.error('[JWT] Token is expired');
      return false;
    }

    return true;
  } catch (error) {
    console.error('[JWT] Error validating Cognito token:', error);
    return false;
  }
}

/**
 * Decodes a Cognito JWT token and returns the payload
 * @param token - The JWT token to decode
 * @returns The decoded token payload or null if invalid
 */
export function decodeCognitoToken(token: string): CognitoJWTPayload | null {
  try {
    if (!isValidCognitoToken(token)) {
      return null;
    }

    return jwtDecode<CognitoJWTPayload>(token);
  } catch (error) {
    console.error('[JWT] Error decoding Cognito token:', error);
    return null;
  }
}

/**
 * Extracts token expiration time in seconds
 * @param token - The JWT token
 * @returns Expiration time in seconds or null if invalid
 */
export function getTokenExpirationTime(token: string): number | null {
  try {
    const decoded = decodeCognitoToken(token);
    return decoded ? decoded.exp : null;
  } catch (error) {
    console.error('[JWT] Error getting token expiration:', error);
    return null;
  }
}

/**
 * Checks if a token will expire within the specified number of seconds
 * @param token - The JWT token
 * @param bufferSeconds - Number of seconds before expiration to consider as "expiring soon"
 * @returns boolean indicating if token is expiring soon
 */
export function isTokenExpiringSoon(token: string, bufferSeconds: number = 300): boolean {
  try {
    const expirationTime = getTokenExpirationTime(token);
    if (!expirationTime) {
      return true; // Consider invalid tokens as expiring
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return expirationTime < (currentTime + bufferSeconds);
  } catch (error) {
    console.error('[JWT] Error checking token expiration:', error);
    return true;
  }
}